import { ButtonAsLink } from '@cluey/cluey-components';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ClueyLoaderWhite from '../../../../../../assets/images/cluey-loader-white.gif';
import LockOrange from '../../../../../../assets/images/icon-lock-orange.svg';
import { InfoBox } from '../../../../../../components/hub/home/InfoBox';
import { getZuoraPrefill } from '../../../../../../selectors/zuoraSelector';
import { PaymentService } from '../../../../../../services/PaymentService';
import {
  fetchZuoraParams as fetchZuoraParamsAction,
  resetZuoraParams as resetZuoraParamsAction,
  setZuoraSuccessMsg,
} from './fetchZuora';

const ZUORA_SCRIPT_URL = 'https://static.zuora.com/Resources/libs/hosted/1.3.1/zuora-min.js';

// Extend Window for Zuora global
declare global {
  interface Window {
    Z?: {
      render: (params: ZuoraParams, prefill: ZuoraPrefill, callback: (response: { refId: string }) => void) => void;
      setEventHandler: (event: string, callback: () => void) => void;
    };
  }
}

type ZuoraParams = {
  id: string;
  key: string;
  signature: string;
  style?: string;
  submitEnabled?: boolean;
  tenantId: string;
  token: string;
  url: string;
  field_accountId?: string;
};

type ZuoraPrefill = {
  creditCardCity?: string;
  creditCardPostalCode?: string;
  creditCardCountry?: string;
};

export interface PaymentMethodFormProps {
  zuoraAccountId: string;
  onCancel: () => void;
  onSuccess?: () => void;
}

function loadZuoraScript(): Promise<HTMLScriptElement> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = ZUORA_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error('Failed to load Zuora script'));
    document.body.appendChild(script);
  });
}

function unloadZuoraScript(script: HTMLScriptElement): void {
  script.remove();
  (window as unknown as { Z?: unknown }).Z = undefined;
}

function renderZuoraForm(params: ZuoraParams, prefill: ZuoraPrefill): Promise<() => Promise<{ refId: string }>> {
  return new Promise((resolve, reject) => {
    if (typeof window.Z === 'undefined') {
      reject(new Error('Zuora JS not loaded'));
      return;
    }
    const renderPromise = new Promise<{ refId: string }>((renderResolve, renderReject) => {
      try {
        window.Z!.render(params, prefill, (response) => renderResolve(response));
      } catch (error) {
        (error as Error & { isZuoraJSError?: boolean }).isZuoraJSError = true;
        renderReject(error);
      }
    });
    window.Z!.setEventHandler('onloadCallback', () => {
      resolve(() => renderPromise);
    });
  });
}

export const PaymentMethodForm = ({ zuoraAccountId, onCancel, onSuccess }: PaymentMethodFormProps) => {
  const dispatch = useDispatch();
  const zuoraParams = useSelector((state: { zuora: { params: ZuoraParams | null } }) => state.zuora?.params);
  const zuoraError = useSelector((state: { zuora: { error: string } }) => state.zuora?.error ?? '');
  const prefill = useSelector(getZuoraPrefill);

  const [status, setStatus] = useState<'loading-script' | 'loading-form' | 'ready' | 'submitting'>('loading-script');
  const [errorMessage, setErrorMessage] = useState('');
  const [scriptLoaded, setScriptLoaded] = useState(false);

  const cancelledRef = useRef(false);

  const fetchZuoraParams = useCallback(() => {
    dispatch(fetchZuoraParamsAction('manageSubscriptionPage'));
  }, [dispatch]);

  const resetZuoraParams = useCallback(() => {
    dispatch(resetZuoraParamsAction());
  }, [dispatch]);

  const setZuoraSuccess = useCallback(() => {
    dispatch(setZuoraSuccessMsg());
  }, [dispatch]);

  // Load Zuora script on mount; on unmount remove script, clear global, reset Redux zuora params
  useEffect(() => {
    let scriptTag: HTMLScriptElement | null = null;

    loadZuoraScript()
      .then((script) => {
        if (!cancelledRef.current) {
          scriptTag = script;
          setScriptLoaded(true);
        } else {
          unloadZuoraScript(script);
        }
      })
      .catch(() => {
        if (!cancelledRef.current) {
          setErrorMessage('Failed to load payment form. Please try again later.');
          setStatus('ready');
        }
      });

    return () => {
      cancelledRef.current = true;
      if (scriptTag) {
        unloadZuoraScript(scriptTag);
      }
      resetZuoraParams();
    };
  }, [resetZuoraParams]);

  // Fetch params, render form, handle submission
  useEffect(() => {
    cancelledRef.current = false;
    const runFlow = async () => {
      if (!zuoraParams || isEmpty(zuoraParams) || !zuoraAccountId || !scriptLoaded) {
        if (zuoraError) {
          setErrorMessage(zuoraError);
          setStatus('ready');
        } else if (zuoraParams === null && scriptLoaded) {
          fetchZuoraParams();
        }
        return;
      }

      setErrorMessage('');
      setStatus('loading-form');

      try {
        const paramsWithAccount: ZuoraParams = {
          ...zuoraParams,
          field_accountId: zuoraAccountId,
        };

        const getSubmitPromise = await renderZuoraForm(paramsWithAccount, prefill);
        if (cancelledRef.current) return;

        setStatus('ready');

        const { refId } = await getSubmitPromise();

        if (cancelledRef.current) return;

        setStatus('submitting');

        await PaymentService.setPrimaryPaymentMethod(zuoraAccountId, refId);
        if (cancelledRef.current) return;

        resetZuoraParams();
        setZuoraSuccess();
        onSuccess?.();
      } catch (error) {
        if (cancelledRef.current) return;

        const err = error as Error & { isZuoraJSError?: boolean };
        if (!err.isZuoraJSError) {
          console.warn('Payment form error:', err);
          setErrorMessage(err.message ?? 'An unexpected error occurred. Please try again.');
        }
        setStatus('ready');
      }
    };

    runFlow();

    return () => {
      cancelledRef.current = true;
    };
  }, [
    zuoraParams,
    zuoraError,
    zuoraAccountId,
    scriptLoaded,
    prefill,
    fetchZuoraParams,
    resetZuoraParams,
    setZuoraSuccess,
    onSuccess,
  ]);

  const isLoading = status === 'loading-script' || status === 'loading-form' || status === 'submitting';

  return (
    <>
      {isLoading ? (
        <div className="flex justify-center">
          <img src={ClueyLoaderWhite} alt="Loading" className="h-[100px]" />
        </div>
      ) : (
        <div className="block">
          <h2 className="flex items-center text-sm text-slate-5">
            <img src={LockOrange} alt="" height={20} className="pr-4" /> We use 128-bit SSL encryption to secure your
            details
          </h2>
        </div>
      )}

      <div>
        {errorMessage && <InfoBox type="error" body={errorMessage} />}
        <div id="zuora_payment" style={{ opacity: isLoading ? 0 : 1 }} />
      </div>

      {!isLoading && (
        <div className="p-4">
          <ButtonAsLink size="lg" onClick={onCancel}>
            Cancel
          </ButtonAsLink>
        </div>
      )}
    </>
  );
};
